import { useEffect } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { ViewTabs } from "@/components/ViewTabs";
import { ShellVerdict } from "@/components/ShellVerdict";
import { FreshnessPip } from "@/components/ui/FreshnessPip";
import { ScopeChip } from "@/components/ui/ScopeChip";
import { useSiteStore, useTopology } from "@/store/site";
import { useTbStatePush } from "@/hooks/useTbStatePush";
import { setBaseTitle } from "@/lib/docTitle";
import { formatTimeOfDay } from "@/lib/format";
import { announce } from "@/hooks/useAnnouncer";
import { tr, useT } from "@/lib/i18n";

/** Browser UTC offset as "UTC+2" / "UTC−5:30" for the timezone chip. */
function utcOffsetLabel(): string {
  const min = -new Date().getTimezoneOffset();
  const sign = min < 0 ? "−" : "+";
  const abs = Math.abs(min);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}

/**
 * Standard page chrome: the two-tier ViewTabs nav, then a title row +
 * caption + optional actions, then a 4-spaced stack of content slots.
 *
 * The title row also carries a compact "data as of HH:MM:SS · pip"
 * readout fed from the newest store snapshot (OVW-04 / CTL-10 / CONS-08):
 * every page declares its data age, including pages that previously had
 * no freshness affordance at all. The pip's thresholds are cadence-aware
 * (see FreshnessPip).
 *
 * The title row and content stack get one orchestrated reveal per route
 * via the `.route-reveal` CSS class (staggered fade-up). It's deliberately
 * CSS-driven, not JS — a CSS animation can never leave content stuck
 * invisible, and `prefers-reduced-motion` neutralises it automatically.
 * ViewTabs sits outside `.route-reveal` because it is persistent chrome
 * and shouldn't re-fade on every navigation.
 */
export function PageShell({
  title,
  caption,
  actions,
  showScope = true,
  children,
}: PropsWithChildren<{ title: string; caption?: ReactNode; actions?: ReactNode; showScope?: boolean }>) {
  // Newest snapshot timestamp — primitive selector so the shell re-renders
  // on data ticks without forcing a new object identity per select.
  const snapTs = useSiteStore((s) => s.snapshot.ts);
  // Site IANA timezone for the chip below (DSGN-15). Adapters return a
  // stable topology object, so this selector doesn't churn renders; an
  // absent/empty field simply hides the chip.
  const siteTz = useTopology().timezone;
  const t = useT();
  // Outbound TB bridge (DOCS-04): mirror this route into the host
  // dashboard's state so the TB URL captures console navigation. No-op
  // outside the tb-host-widget iframe.
  useTbStatePush();

  // Browser-tab title per route ("Alarms · smart-e") so standalone
  // operators can tell windows apart in the task switcher / tab strip.
  // Every page renders through PageShell, so one effect covers all routes.
  // Routed through setBaseTitle — lib/docTitle is the ONLY document.title
  // writer, so the alarm-badge prefix "(2🔴)" composes instead of being
  // clobbered. (In TB-embedded mode the host page's title wins — harmless.)
  useEffect(() => {
    setBaseTitle(`${title} · smart-e`);
    // G28: SPA route changes are silent to screen readers — announce the
    // destination politely so AT users know where navigation landed.
    announce(tr("a11y.nowViewing", { title }));
  }, [title]);

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <ViewTabs />
      <div className="route-reveal">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {/* N02 + N09 — the persistent GM verdict (health · saved €X ·
                active mode), carried into the shell so off-Overview routes
                still answer "is it ok / making money / what's it doing"
                without navigating. IN-FLOW (not fixed/sticky): it sits in the
                normal title-row flow, flex-wraps inside the narrow TB column,
                and collides with none of the top-12 fixed strips. Self-
                suppresses on /overview + /controller and whenever a live
                critical IncidentStrip owns the surface (see ShellVerdict). */}
            <ShellVerdict />
            <h1 className="text-xl font-semibold tracking-tight text-text">{title}</h1>
            {caption && <p className="mt-1 text-xs text-text-muted">{caption}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* F20 scope chip — self-gates to a pinned custom window, so it's
                invisible on the common relative-preset default. The three
                TimeRangeBar pages pass showScope={false} to avoid duplicating
                their own bar. */}
            {showScope !== false && <ScopeChip />}
            {/* Timezone disclosure on every timestamp surface (DSGN-15) —
                reuses the wording HistoricalPage established. */}
            {siteTz && (
              <span
                className="hidden text-[10px] text-text-muted md:inline"
                title={t("shell.tz.disclosure", { offset: utcOffsetLabel(), tz: siteTz })}
              >
                {t("shell.tz.inline", { offset: utcOffsetLabel(), tz: siteTz })}
              </span>
            )}
            <span
              className="flex items-center gap-1.5 text-[10px] text-text-muted"
              title={t("shell.dataAsOf.title")}
            >
              <span className="hidden md:inline">
                {t("shell.dataAsOf.label")} <span className="num text-text">{formatTimeOfDay(snapTs)}</span>
              </span>
              <FreshnessPip ts={snapTs} size="xs" />
            </span>
            {actions}
          </div>
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}
